import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable, of, map, firstValueFrom } from 'rxjs';
import { CommonService } from './common.service';
import { PreviewModeService } from './preview-mode.service';
import { Constants } from 'src/app/config/app.constants';
import { ContentfulService } from './contentful.service';
import { GET_NT_Experiences } from 'src/app/gql/ninetailed-experience';
import { GET_NT_Audiences } from 'src/app/gql/ninetailed-audience';

type OnSelectVariantState<T> = {
  status: 'loading' | 'success' | 'error';
  loading: boolean;
  hasVariants: boolean;
  baseline: T;
  variant: T;
  variantIndex: number;
  isPersonalized: boolean;
  experience: unknown;
  audience: { id: string } | null;
  profile: unknown;
  error: Error | null;
};

type NinetailedInstance = {
  page: (data?: Record<string, unknown>) => Promise<unknown>;
  track: (event: string, properties?: Record<string, unknown>) => Promise<unknown>;
  identify: (uid: string, traits?: Record<string, unknown>) => Promise<unknown>;
  reset: () => Promise<void>;
  onProfileChange: (cb: (state: ProfileState) => void) => () => void;
  onSelectVariant: <T extends { id: string }>(
    args: { baseline: T; experiences: ExperienceConfig<T>[] },
    cb: (state: OnSelectVariantState<T>) => void
  ) => () => void;
  observeElement: (payload: {
    element: Element;
    variant: { id: string; [k: string]: unknown };
    variantIndex: number;
    experience?: { id: string; type: string; name?: string } | null;
    audience?: { id: string; name?: string } | null;
  }, options?: { delay?: number }) => void;
  unobserveElement: (element: Element) => void;
};

export type ProfileState = {
  status: 'loading' | 'success' | 'error';
  profile: Record<string, unknown> | null;
  experiences: unknown[] | null;
  error: unknown | null;
};

export interface ExperienceConfig<T extends { id: string } = { id: string }> {
  id: string;
  type: 'nt_personalization' | 'nt_experiment';
  name?: string;
  description?: string;
  audience?: { id: string };
  trafficAllocation: number;
  distribution: { index: number; start: number; end: number }[];
  components: {
    type: 'EntryReplacement';
    baseline: { id: string };
    variants: T[];
  }[];
}

export interface NtExperience {
  sys: { id: string };
  ntName: string;
  ntType: 'nt_experiment' | 'nt_personalization';
  ntConfig: {
    traffic: number;
    distribution: number[];
    components: { baseline: { id: string }; variants: { id: string; hidden: boolean }[] }[];
  };
  ntExperienceId: string;
  ntAudience: {
    sys: { id: string };
    ntAudienceId: string;
    ntRules: unknown;
  } | null;
  ntVariantsCollection: {
    items: Record<string, unknown>[];
  };
}

export interface NtAudience {
  sys: { id: string };
  ntAudienceId: string;
  ntName: string;
  ntDescription: string;
  ntRules: unknown;
}

const INITIAL_PROFILE_STATE: ProfileState = {
  status: 'loading',
  profile: null,
  experiences: null,
  error: null,
};

@Injectable({
  providedIn: 'root'
})
export class NinetailedService implements OnDestroy {
  private instance: NinetailedInstance | null = null;
  private initialized = false;
  private trackedExperienceEvents = new Set<string>();
  private pendingExperienceEvents: Array<Record<string, string>> = [];
  private oneTrustListenersBound = false;
  private oneTrustObserver: MutationObserver | null = null;
  private oneTrustFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly oneTrustFallbackTimeoutMs = 10000;
  private experiencesCache: NtExperience[] | null = null;
  private audiencesCache: NtAudience[] | null = null;
  private detachListeners: (() => void)[] = [];
  private readyResolve!: () => void;
  private readyPromise = new Promise<void>(resolve => { this.readyResolve = resolve; });

  private pageEntryIds: string[] = [];
  private pageEntryIdsResolve: ((ids: string[]) => void) | null = null;
  private pageEntryIdsPromise = new Promise<string[]>(resolve => { this.pageEntryIdsResolve = resolve; });

  readonly profileState$ = new BehaviorSubject<ProfileState>(INITIAL_PROFILE_STATE);

  constructor(
    private commonService: CommonService,
    private ngZone: NgZone,
    private constants: Constants,
    private contentfulService: ContentfulService,
    private previewModeService: PreviewModeService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  async initialize(): Promise<void> {
    if (this.initialized || !this.commonService.isPlatformBrowser()) {
      return;
    }
    this.initialized = true;

    // Pre-fetch experiences in parallel with SDK chunk download (skip in preview — it fetches with preview flag below)
    if (!this.isPersonalizationPreview() && !this.previewModeService.isPreviewMode()) {
      this.fetchExperiences().subscribe({ error: () => { /* cache miss handled by applyPersonalization */ } });
    }

    const { Ninetailed } = await import('@ninetailed/experience.js');

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const plugins: any[] = [];

    if (this.isPersonalizationPreview()) {
      try {
        const [{ NinetailedPreviewPlugin }, experiences, audiences] = await Promise.all([
          import('@ninetailed/experience.js-plugin-preview'),
          firstValueFrom(this.fetchExperiences({ preview: true })),
          firstValueFrom(this.fetchAudiences({ preview: true })),
        ]);

        const mappedExperiences = this.mapExperiencesFromGql(experiences) as any[];
        const pageEntryIds = await this.waitForPageEntryIds();
        const pageExperiences = pageEntryIds.length
          ? mappedExperiences.filter(e =>
              Array.isArray(e?.components) &&
              e.components.some((c: any) => pageEntryIds.includes(c?.baseline?.id))
            )
          : mappedExperiences;
        const referencedAudienceIds = new Set(
          pageExperiences.map(e => e?.audience?.id).filter(Boolean)
        );
        const mappedAudiences = audiences
          .filter(a => referencedAudienceIds.has(a.ntAudienceId))
          .map(a => ({
            id: a.ntAudienceId,
            name: a.ntName,
            description: a.ntDescription,
          }));

        plugins.push(
          new NinetailedPreviewPlugin({
            experiences: pageExperiences,
            audiences: mappedAudiences,
            ui: { opener: { hide: false } },
          })
        );
      } catch (err) {
        console.error('Failed to load Ninetailed Preview Plugin:', err);
      }
    }

    if (!this.previewModeService.isPreviewMode()) {
      try {
        const { NinetailedInsightsPlugin } = await import('@ninetailed/experience.js-plugin-insights');
        plugins.push(new NinetailedInsightsPlugin());
      } catch (err) {
        console.error('Failed to load Ninetailed Insights Plugin:', err);
      }

      try {
        const { NinetailedGoogleTagmanagerPlugin } = await import('@ninetailed/experience.js-plugin-google-tagmanager');
        plugins.push(new NinetailedGoogleTagmanagerPlugin({
          template: {
            ninetailed_audience_name: '{{ audience.name }}',
          },
        }));
      } catch (err) {
        console.error('Failed to load Ninetailed Google Tag Manager Plugin:', err);
      }
    }

    const ninetailed = new Ninetailed(
      {
        clientId: this.constants.NinetailedClientId,
        environment: this.constants.NinetailedEnvironment,
      },
      {
        plugins,
        componentViewTrackingThreshold: 2000,
      }
    );

    ninetailed.onProfileChange((state: ProfileState) => {
      this.ngZone.run(() => {
        this.profileState$.next(state);
      });
    });

    this.instance = ninetailed as unknown as NinetailedInstance;
    // Trigger initial profile resolution — required for onSelectVariant to move from 'loading' to 'success'.
    // Resolve readyPromise only after page() completes so that onSelectVariant listeners
    // registered after whenReady() fire immediately with 'success' (profile already resolved).
    this.trackedExperienceEvents.clear();
    this.instance.page().then(
      () => this.readyResolve(),
      () => this.readyResolve()
    );
  }

  /**
   * Returns a promise that resolves once the SDK has finished initializing.
   */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  /**
   * Reports the content entry IDs present on the current page (page content and the
   * site header). Used to scope the preview widget to experiences whose baseline lives
   * on this page. Reporters can call this independently — IDs are merged (union).
   */
  registerPageEntryIds(ids: string[]): void {
    if (!this.isPersonalizationPreview()) {
      return;
    }
    if (ids?.length) {
      const merged = new Set(this.pageEntryIds);
      for (const id of ids) {
        if (id) {
          merged.add(id);
        }
      }
      this.pageEntryIds = Array.from(merged);
    }
    if (this.pageEntryIdsResolve) {
      this.pageEntryIdsResolve(this.pageEntryIds);
      this.pageEntryIdsResolve = null;
    }
  }

  /**
   * Waits for the current page's content entry IDs to be reported, falling back to
   * whatever has been reported so far after a short timeout so preview init never stalls.
   * After the first report, waits a brief settle window so independent reporters (page
   * content and the header) can all register before the widget is built.
   */
  private async waitForPageEntryIds(timeoutMs = 2500, settleMs = 400): Promise<string[]> {
    let timerId: ReturnType<typeof setTimeout>;
    await Promise.race([
      this.pageEntryIdsPromise,
      new Promise<string[]>(resolve => { timerId = setTimeout(() => resolve(this.pageEntryIds), timeoutMs); })
    ]).finally(() => clearTimeout(timerId));
    await new Promise<void>(resolve => setTimeout(resolve, settleMs));
    return this.pageEntryIds;
  }

  isPreviewSession(): boolean {
    return this.isPersonalizationPreview() || this.previewModeService.isPreviewMode();
  }

  async page(data?: Record<string, unknown>): Promise<void> {
    if (!this.commonService.isPlatformBrowser()) {
      return;
    }
    if (!this.instance) {
      let timerId: ReturnType<typeof setTimeout>;
      await Promise.race([
        this.readyPromise,
        new Promise<void>(r => { timerId = setTimeout(r, 5000); })
      ]).finally(() => clearTimeout(timerId));
    }
    if (!this.instance) {
      return;
    }
    this.trackedExperienceEvents.clear();
    await this.instance.page(data);
  }

  async track(event: string, properties?: Record<string, unknown>): Promise<void> {
    if (!this.commonService.isPlatformBrowser()) {
      return;
    }
    if (!this.instance) {
      let timerId: ReturnType<typeof setTimeout>;
      await Promise.race([
        this.readyPromise,
        new Promise<void>(r => { timerId = setTimeout(r, 5000); })
      ]).finally(() => clearTimeout(timerId));
    }
    if (!this.instance) {
      return;
    }
    await this.instance.track(event, properties);
  }

  async identify(uid: string, traits?: Record<string, unknown>): Promise<void> {
    if (!this.commonService.isPlatformBrowser() || !this.instance) {
      return;
    }
    // Guard against passing the anonymous profile id as uid.
    // Doing so triggers a self-merge cycle in the Experience API, breaking profile continuity.
    const anonId = this.getAnonymousId();
    if (uid && anonId && uid === anonId) {
      console.warn('[NinetailedService] identify() called with the anonymous profile id; skipping to avoid self-merge.');
      return;
    }
    await this.instance.identify(uid, traits);
  }

  private getAnonymousId(): string | null {
    try {
      return localStorage.getItem('nt_anonymous_id')
        || localStorage.getItem('__nt_anonymous_id__')
        || localStorage.getItem('__anon_id')
        || null;
    } catch {
      return null;
    }
  }

  async reset(): Promise<void> {
    if (!this.commonService.isPlatformBrowser() || !this.instance) {
      return;
    }
    await this.instance.reset();
  }

  ngOnDestroy(): void {
    this.detachSelectVariantListeners();
    if (this.oneTrustListenersBound) {
      this.document.defaultView?.removeEventListener('OneTrustGroupsUpdated', this.handleOneTrustUpdate as EventListener);
      this.oneTrustListenersBound = false;
    }
    this.oneTrustObserver?.disconnect();
    this.oneTrustObserver = null;
    if (this.oneTrustFallbackTimer) {
      clearTimeout(this.oneTrustFallbackTimer);
      this.oneTrustFallbackTimer = null;
    }
  }

  /**
   * Fetches all active Ninetailed experiences from Contentful.
   * Results are cached in memory after the first fetch.
   */
  fetchExperiences(variables: Record<string, unknown> = {}): Observable<NtExperience[]> {
    if (this.experiencesCache) {
      return of(this.experiencesCache);
    }
    if (!('preview' in variables) && this.previewModeService.isPreviewMode()) {
      variables = { ...variables, preview: true };
    }
    if (!('domain' in variables)) {
      variables = { ...variables, domain: this.constants.domainFilter };
    }
    return this.contentfulService.getPageData(GET_NT_Experiences, variables).pipe(
      map(({ data }: { data: { ntExperienceCollection: { items: NtExperience[] } } }) => {
        const experiences = data?.ntExperienceCollection?.items || [];
        this.experiencesCache = experiences;
        return experiences;
      })
    );
  }

  /**
   * Fetches all Ninetailed audiences from Contentful.
   * Results are cached in memory after the first fetch.
   */
  fetchAudiences(variables: Record<string, unknown> = {}): Observable<NtAudience[]> {
    if (this.audiencesCache) {
      return of(this.audiencesCache);
    }
    return this.contentfulService.getPageData(GET_NT_Audiences, variables).pipe(
      map(({ data }: { data: { ntAudienceCollection: { items: NtAudience[] } } }) => {
        const audiences = data?.ntAudienceCollection?.items || [];
        this.audiencesCache = audiences;
        return audiences;
      })
    );
  }

  /**
   * Transforms Contentful GQL experience entries into the ExperienceConfiguration
   * format expected by the Ninetailed SDK's onSelectVariant.
   */
  mapExperiencesFromGql(experiences: NtExperience[]): ExperienceConfig[] {
    const mapped: ExperienceConfig[] = [];
    for (const exp of experiences) {
      try {
        const dist = exp.ntConfig?.distribution || [];
        let cursor = 0;
        const distribution = dist.map((weight, index) => {
          const start = cursor;
          cursor += weight;
          return { index, start, end: cursor };
        });

        const variants = exp.ntVariantsCollection?.items || [];
        const variantLookup = new Map<string, Record<string, unknown>>();
        for (const v of variants) {
          const sid = (v as { sys?: { id?: string } }).sys?.id;
          if (sid) {
            // Strip isPzd from variant data: the personalized component uses isPzd on the
            // baseline to keep its loader visible; the variant must NOT carry isPzd so that
            // when the SDK swaps it in, the loader is removed.
            const clone: Record<string, unknown> = { ...v };
            delete clone['isPzd'];
            variantLookup.set(sid, clone);
          }
        }

        mapped.push({
          id: exp.ntExperienceId || exp.sys.id,
          type: exp.ntType,
          name: exp.ntName,
          audience: exp.ntAudience ? { id: exp.ntAudience.ntAudienceId } : undefined,
          trafficAllocation: exp.ntConfig?.traffic ?? 1,
          distribution,
          components: (exp.ntConfig?.components || []).map(comp => ({
            type: 'EntryReplacement' as const,
            baseline: { id: comp.baseline.id },
            // Filter out non-hidden variants with no resolved data — matches Ninetailed ExperienceMapper reference
            variants: comp.variants
              .map(v => {
                if (v.hidden) return { id: v.id, hidden: true };
                const data = variantLookup.get(v.id);
                if (!data) return null;
                return { ...data, id: v.id, hidden: false };
              })
              .filter((v): v is { id: string; hidden: boolean } => v !== null),
          })),
        });
      } catch (err) {
        console.warn('[NinetailedService] Skipping malformed experience:', exp?.sys?.id, err);
      }
    }
    return mapped;
  }

  /**
   * Uses the SDK's onSelectVariant to resolve which variant to show for a content area item.
   * The SDK handles audience matching, traffic allocation, and automatic impression tracking.
   *
   * @param baseline The baseline content item (must have sys.id)
   * @param mappedExperiences The mapped experience configurations
   * @param onVariantSelected Callback fired when the SDK selects a variant
   * @returns A cleanup function to detach the listener
   */
  observeVariant(
    baseline: any,
    mappedExperiences: ExperienceConfig[],
    onVariantSelected: (state: OnSelectVariantState<any>) => void
  ): () => void {
    if (!this.instance) {
      return () => void 0;
    }
    const baselineRef = { ...baseline, id: baseline.sys?.id || baseline.id };
    const detach = this.instance.onSelectVariant(
      { baseline: baselineRef, experiences: mappedExperiences },
      (state) => {
        if (!state.loading) {
          this.pushGtmExperienceEvent(state);
        }
        this.ngZone.run(() => onVariantSelected(state));
      }
    );
    this.detachListeners.push(detach);
    return detach;
  }

  detachSelectVariantListeners(): void {
    this.detachListeners.forEach(fn => fn());
    this.detachListeners = [];
  }

  /**
   * Registers a DOM element for component view tracking via the Insights Plugin.
   * The Insights Plugin will fire a 'component' event when the element enters the viewport.
   */
  observeElement(payload: {
    element: Element;
    variant: { id: string; [k: string]: unknown };
    variantIndex: number;
    experience?: { id: string; type: string; name?: string } | null;
    audience?: { id: string; name?: string } | null;
  }, options?: { delay?: number }): void {
    if (!this.instance || typeof this.instance.observeElement !== 'function') {
      return;
    }
    this.instance.observeElement(payload, options);
  }

  /**
   * Unregisters a DOM element from component view tracking.
   */
  unobserveElement(element: Element): void {
    if (!this.instance || typeof this.instance.unobserveElement !== 'function') {
      return;
    }
    this.instance.unobserveElement(element);
  }

  /**
   * Re-registers a DOM element for component view tracking from a variant state.
   * Shared by page-template and header personalization so the observe payload
   * mapping lives in one place.
   */
  registerElementInsights(element: Element | null, state: {
    variant: { id: string;[k: string]: unknown };
    variantIndex: number;
    experience?: { id?: string; type?: string; name?: string } | null;
    audience?: { id: string; name?: string } | null;
  }): void {
    if (!element) {
      return;
    }
    this.unobserveElement(element);
    const exp = state.experience;
    this.observeElement({
      element,
      variant: { id: state.variant.id },
      variantIndex: state.variantIndex,
      experience: exp?.id ? {
        id: exp.id,
        type: exp.type || 'nt_personalization',
        name: exp.name,
      } : null,
      audience: state.audience || null,
    });
  }

  private isPersonalizationPreview(): boolean {
    try {
      const url = new URL(this.document.URL);
      return url.searchParams.get('ntpreview') === 'true';
    } catch {
      return false;
    }
  }

  private pushGtmExperienceEvent(state: OnSelectVariantState<any>): void {
    if (!this.commonService.isPlatformBrowser()) {
      return;
    }
    const win = this.document.defaultView as (Window & { dataLayer?: unknown[] }) | null;
    if (!win) {
      return;
    }
    const experienceId = (state.experience as { id?: string } | null)?.id;
    const variantId = state.variant?.id;
    const audienceId = state.audience?.id || '';
    if (!experienceId || !variantId) {
      return;
    }
    const selectedVariantSelector = state.variantIndex === 0 ? 'control' : `variant ${state.variantIndex}`;
    const eventKey = `${experienceId}|${variantId}|${selectedVariantSelector}|${audienceId}`;
    if (this.trackedExperienceEvents.has(eventKey)) {
      return;
    }
    this.trackedExperienceEvents.add(eventKey);

    const eventPayload: Record<string, string> = {
      event: 'nt_experience',
      ninetailed_variant: selectedVariantSelector,
      ninetailed_experience: experienceId,
      ninetailed_experience_name: (state.experience as { name?: string } | null)?.name || '',
      ninetailed_audience: audienceId,
      ninetailed_audience_name: (state.audience as { name?: string } | null)?.name || '',
      ninetailed_component: variantId,
    };

    if (!this.isOneTrustReady() || this.isOneTrustBlocking()) {
      this.pendingExperienceEvents.push(eventPayload);
      this.bindOneTrustListeners();
      this.scheduleOneTrustFallback();
      return;
    }

    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push(eventPayload);
  }

  private isOneTrustReady(): boolean {
    const win = this.document.defaultView as (Window & { OneTrust?: unknown }) | null;
    return !!win?.OneTrust || !!this.document.getElementById('onetrust-consent-sdk');
  }

  private isOneTrustBlocking(): boolean {
    const banner = this.document.getElementById('onetrust-banner-sdk');
    if (!banner) {
      return false;
    }
    const style = this.document.defaultView?.getComputedStyle(banner);
    if (!style) {
      return true;
    }
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  private bindOneTrustListeners(): void {
    if (this.oneTrustListenersBound || !this.commonService.isPlatformBrowser()) {
      return;
    }
    const win = this.document.defaultView;
    if (!win) {
      return;
    }
    win.addEventListener('OneTrustGroupsUpdated', this.handleOneTrustUpdate as EventListener);
    this.oneTrustListenersBound = true;

    if (!this.oneTrustObserver && this.document.body) {
      this.oneTrustObserver = new MutationObserver(() => {
        if (this.isOneTrustReady() && !this.isOneTrustBlocking()) {
          this.flushPendingExperienceEvents();
        }
      });
      this.oneTrustObserver.observe(this.document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }
  }

  private scheduleOneTrustFallback(): void {
    if (this.oneTrustFallbackTimer || this.pendingExperienceEvents.length === 0) {
      return;
    }
    this.oneTrustFallbackTimer = setTimeout(() => {
      this.oneTrustFallbackTimer = null;
      // Fallback only when OneTrust failed to initialize; if initialized, keep normal consent flow.
      this.flushPendingExperienceEvents(!this.isOneTrustReady());
    }, this.oneTrustFallbackTimeoutMs);
  }

  private handleOneTrustUpdate = (): void => {
    this.flushPendingExperienceEvents();
  };

  private flushPendingExperienceEvents(forceWhenOneTrustMissing = false): void {
    if (this.pendingExperienceEvents.length === 0) {
      return;
    }
    if ((!forceWhenOneTrustMissing && !this.isOneTrustReady()) || this.isOneTrustBlocking()) {
      return;
    }
    const win = this.document.defaultView as (Window & { dataLayer?: unknown[] }) | null;
    if (!win) {
      return;
    }
    win.dataLayer = win.dataLayer || [];
    for (const eventPayload of this.pendingExperienceEvents) {
      win.dataLayer.push(eventPayload);
    }
    this.pendingExperienceEvents = [];
    if (this.oneTrustFallbackTimer) {
      clearTimeout(this.oneTrustFallbackTimer);
      this.oneTrustFallbackTimer = null;
    }
  }
}
